import { Company } from '../../../../modules/domain/company/types'
import { useCompanyListByExcludedIds, useCompanyListByIds } from '../../../../modules/domain/company/hooks'
import React, { useEffect, useRef, useState } from 'react'
import type { GetProp, TableColumnsType, TableProps } from 'antd'
import { Button, Flex, Table } from 'antd'
import { useTableHeight } from '../../../../hooks/useTableHeight'
import { CaretLeftOutlined, CaretRightOutlined } from '@ant-design/icons'
import { Progress } from '../../../../modules/types'
import { useAction } from '@agro-club/frontend-shared'
import CompanyActions from '../../../../modules/domain/company/duck'
import { useSelector } from 'react-redux'
import CompanySelectors from '../../../../modules/domain/company/selectors'
import CSS from 'csstype'
import CompanySearchInputFilter from '../../../components/Companies/Filters/Company/CompanySearchInputFilter'
import AssetCompanySearchInputFilter from '../../../components/Companies/Filters/AssetCompany/AssetCompanySearchInputFilter'
import CompanyTerritoryFilterSelect from '../../../components/Companies/Filters/Company/CompanyTerritoryFilterSelect'
import AssetCompanyTerritoryFilterSelect from '../../../components/Companies/Filters/AssetCompany/AssetCompanyTerritoryFilterSelect'
import CompanyPartnerTypeFilterSelect from '../../../components/Companies/Filters/Company/CompanyPartnerTypeFilterSelect'
import AssetCompanyPartnerTypeFilterSelect from '../../../components/Companies/Filters/AssetCompany/AssetCompanyPartnerTypeFilterSelect'
import { RowSelectMethod } from 'antd/es/table/interface'
import {
  getCompanyList,
  getCompanyListByIds,
  getCompanyListByExcludedIds,
} from '../../../../modules/domain/company/managers'
import { useTranslation } from 'react-i18next'

type TableTransferProps = {
  selectedCompanyIds: string[]
  selectedCompaniesSetter: (selectedIds: string[]) => void
}

type TableRowSelection<T extends object = object> = TableProps<T>['rowSelection']
type TablePaginationConfig = Exclude<GetProp<TableProps, 'pagination'>, boolean>

const tableStyles: CSS.Properties = {
  flex: 1,
}

const allSelectedStyle: CSS.Properties = {
  color: '#1890ff',
  fontSize: '12px',
  marginTop: '-16px',
  textAlign: 'right',
}

const CompanyTableTransfer: React.FC<TableTransferProps> = ({ selectedCompanyIds, selectedCompaniesSetter }) => {
  const { t } = useTranslation(['digitalAsset', 'common', 'validation'])

  const companyListAction = useAction(CompanyActions.listByExcludedIdsRequested)
  const companyTotal = useSelector(CompanySelectors.listByExcludedIdsTotal)
  const companyPage = useSelector(CompanySelectors.listByExcludedIdsPage)
  const companyPageSize = useSelector(CompanySelectors.listByExcludedIdsPageSize)
  const companyFilter = useSelector(CompanySelectors.listByExcludedIdsFilter)

  //initial catalogs - company
  const [dbCompaniesProgress, dbCompanies = []] = useCompanyListByExcludedIds({
    exclude_companies: selectedCompanyIds,
    filter: companyFilter,
  })

  //asset company catalog
  const assetCompanyListAction = useAction(CompanyActions.listByIdsRequested)
  const assetCompanyTotal = useSelector(CompanySelectors.listByIdsTotal)
  const assetCompanyPage = useSelector(CompanySelectors.listByIdsPage)
  const assetCompanyPageSize = useSelector(CompanySelectors.listByIdsPageSize)
  const assetCompanyFilter = useSelector(CompanySelectors.listByIdsFilter)

  const [digitalAssetCompaniesProgress, digitalAssetCompanies = []] = useCompanyListByIds({
    company_ids: selectedCompanyIds,
    filter: assetCompanyFilter,
  })
  const [selectedCompanies, setSelectedCompanies] = useState<string[]>(selectedCompanyIds)

  // selection lists
  const [catalogSelectedKeys, setCatalogSelectedKeys] = useState<React.Key[]>([])
  const [catalogIsAllSelected, setCatalogIsAllSelected] = useState<boolean>(false)
  const [assetSelectedKeys, setAssetSelectedKeys] = useState<React.Key[]>([])
  const [assetIsAllSelected, setAssetIsAllSelected] = useState<boolean>(false)

  //table height
  const parentRef = useRef<HTMLDivElement | null>(null)
  //todo: value has to be calculated dynamically for 100% responsive
  const additionalContentHeight = 230
  const height = useTableHeight(parentRef, additionalContentHeight)

  //pagination
  const [catalogPagination, setCatalogPagination] = useState<TablePaginationConfig>({
    showSizeChanger: false,
    position: ['bottomRight'],
  })

  const [assetCompanyPagination, setAssetCompanyPagination] = useState<TablePaginationConfig>({
    showSizeChanger: false,
    position: ['bottomRight'],
  })

  const tableColumns: TableColumnsType<Company> = [
    {
      dataIndex: 'official_name',
      title: 'Company',
    },
    {
      title: 'Province',
      render: (_, record: Company) => record?.legal_address.state,
    },
  ]

  const onCatalogChange: TableProps<Company>['onChange'] = (pagination, _filters, _sorter) => {
    setCatalogPagination((prevState) => ({
      ...prevState,
      ...pagination,
    }))
    companyListAction({
      exclude_companies: selectedCompanies,
      page: pagination.current,
      filters: companyFilter,
    })
  }

  const onAssetCompaniesChange: TableProps<Company>['onChange'] = (pagination, _filters, _sorter) => {
    setAssetCompanyPagination((prevState) => ({
      ...prevState,
      ...pagination,
    }))
    assetCompanyListAction({
      company_ids: selectedCompanies,
      page: pagination.current,
      filters: assetCompanyFilter,
    })
  }

  const onCatalogSelectChange = async (
    newSelectedRowKeys: React.Key[],
    _newSelectedRows: Company[],
    info: {
      type: RowSelectMethod
    },
  ) => {
    //todo: add loading animation somewhere since it takes some time
    if (info.type == 'all') {
      await onCatalogAllSelected()
      return
    }
    setCatalogSelectedKeys(newSelectedRowKeys)
  }

  const onCatalogAllSelected = async () => {
    if (catalogIsAllSelected) {
      setCatalogIsAllSelected(false)
      setCatalogSelectedKeys([])
      return
    }

    const companyList = await getCompanyListByExcludedIds(selectedCompanies, 1, companyFilter, 9999)
    const allIDs = companyList.data.map((company) => company.id)
    setCatalogIsAllSelected(true)
    setCatalogSelectedKeys(allIDs)
  }

  const onAssetCompaniesSelectedSelectChange = async (
    newSelectedRowKeys: React.Key[],
    _newSelectedRows: Company[],
    info: {
      type: RowSelectMethod
    },
  ) => {
    if (info.type == 'all') {
      await onAssetCompaniesAllSelected()
      return
    }
    setAssetSelectedKeys(newSelectedRowKeys)
  }

  const onAssetCompaniesAllSelected = async () => {
    if (assetIsAllSelected) {
      setAssetIsAllSelected(false)
      setAssetSelectedKeys([])
      return
    }
    // Fetch all matching IDs so every page shows the correct selection state (preserveSelectedRowKeys)
    const companyList = await getCompanyListByIds(selectedCompanies, 1, assetCompanyFilter, 9999)
    const allIDs = companyList.data.map((company) => company.id)
    setAssetIsAllSelected(true)
    setAssetSelectedKeys(allIDs)
  }

  const catalogRowSelection: TableRowSelection<Company> = {
    selectedRowKeys: catalogSelectedKeys,
    onChange: onCatalogSelectChange,
    preserveSelectedRowKeys: true,
  }

  const assetCompaniesSelectedRowSelection: TableRowSelection<Company> = {
    selectedRowKeys: assetSelectedKeys,
    onChange: onAssetCompaniesSelectedSelectChange,
    preserveSelectedRowKeys: true,
  }

  useEffect(() => {
    if (dbCompanies) {
      setCatalogPagination((prevState) => ({
        ...prevState,
        current: companyPage,
        pageSize: companyPageSize,
        total: companyTotal,
      }))
    }
  }, [companyPage, companyPageSize, companyTotal, dbCompanies])

  useEffect(() => {
    if (digitalAssetCompanies) {
      setAssetCompanyPagination((prevState) => ({
        ...prevState,
        current: assetCompanyPage,
        pageSize: assetCompanyPageSize,
        total: assetCompanyTotal,
      }))
    }
  }, [assetCompanyPage, assetCompanyPageSize, assetCompanyTotal, digitalAssetCompanies])

  const transferToAssetSelected = async () => {
    if (catalogIsAllSelected || (catalogSelectedKeys && catalogSelectedKeys.length > 0)) {
      let selectedIds: string[] = []

      if (catalogIsAllSelected) {
        // When select all is enabled, we need to get all company IDs that match the current filter
        // We'll make a special API call to get all company IDs with a very large page size
        const currentFilter = {
          ...companyFilter,
          exclude_companies: selectedCompanies,
        }
        const companyList = await getCompanyList(currentFilter, {}, 1, 9999)
        const allIDs = companyList.data.map((company) => company.id)
        selectedIds = [...selectedCompanies, ...allIDs]
      } else {
        selectedIds = [...selectedCompanies, ...catalogSelectedKeys.map((key) => `${key}`)]
      }

      const uniqueCompanyIds = [...new Set(selectedIds)]

      assetCompanyListAction({ company_ids: uniqueCompanyIds })
      companyListAction({ exclude_companies: uniqueCompanyIds })

      selectedCompaniesSetter(uniqueCompanyIds)
      setSelectedCompanies(uniqueCompanyIds)
      setCatalogSelectedKeys([])
      setCatalogIsAllSelected(false)

      // Force refresh both tables to show updated data
      companyListAction({ exclude_companies: uniqueCompanyIds, page: 1 })
      assetCompanyListAction({ company_ids: uniqueCompanyIds, page: 1 })
    }
  }

  const transferToCatalog = async () => {
    if (assetIsAllSelected || (assetSelectedKeys && assetSelectedKeys.length > 0)) {
      let remainSelectedData: string[] = []

      if (assetIsAllSelected) {
        // When select all is enabled, we need to get all company IDs that match the current filter
        // We'll make a special API call to get all company IDs with a very large page size
        const companyList = await getCompanyListByIds(selectedCompanies, 1, assetCompanyFilter, 9999)
        const allIDs = companyList.data.map((company) => company.id)
        remainSelectedData = selectedCompanies.filter((id) => !allIDs.includes(id))
      } else {
        // Remove only the selected companies
        remainSelectedData = selectedCompanies.filter((id) => !assetSelectedKeys.includes(id))
      }

      assetCompanyListAction({ company_ids: remainSelectedData })
      selectedCompaniesSetter(remainSelectedData)
      companyListAction({ exclude_companies: remainSelectedData })
      setSelectedCompanies(remainSelectedData)

      setAssetSelectedKeys([])
      setAssetIsAllSelected(false)

      // Force refresh both tables to show updated data
      companyListAction({ exclude_companies: remainSelectedData, page: 1, filters: companyFilter })
      assetCompanyListAction({ company_ids: remainSelectedData, page: 1, filters: assetCompanyFilter })
    }
  }

  return (
    <Flex flex={1} gap="middle" justify="space-between" ref={parentRef}>
      <Flex flex={1} vertical gap="small">
        <CompanySearchInputFilter excludeCompanyIds={selectedCompanyIds} />
        <CompanyTerritoryFilterSelect excludeCompanyIds={selectedCompanyIds} />
        <CompanyPartnerTypeFilterSelect excludeCompanyIds={selectedCompanyIds} />
        <Table
          rowSelection={catalogRowSelection}
          style={tableStyles}
          columns={tableColumns}
          dataSource={dbCompanies}
          rowKey={(record) => record.id}
          loading={dbCompaniesProgress === Progress.WORK}
          scroll={dbCompanies && dbCompanies.length > 0 ? { y: height } : {}}
          pagination={catalogPagination}
          onChange={onCatalogChange}
        />
        {catalogIsAllSelected && (
          <div style={allSelectedStyle}>{t('digitalAsset:form.allCompaniesSelected', { total: companyTotal })}</div>
        )}
      </Flex>
      <Flex vertical justify="center" align="center" gap="middle">
        <Button
          onClick={transferToAssetSelected}
          icon={<CaretRightOutlined />}
          disabled={!(catalogIsAllSelected || (catalogSelectedKeys && catalogSelectedKeys.length !== 0))}
        />
        <Button
          onClick={transferToCatalog}
          icon={<CaretLeftOutlined />}
          disabled={!(assetIsAllSelected || (assetSelectedKeys && assetSelectedKeys.length !== 0))}
        />
      </Flex>
      <Flex flex={1} vertical gap="small">
        <AssetCompanySearchInputFilter companyIds={selectedCompanies} />
        <AssetCompanyTerritoryFilterSelect companyIds={selectedCompanies} />
        <AssetCompanyPartnerTypeFilterSelect companyIds={selectedCompanies} />
        <Table
          rowSelection={assetCompaniesSelectedRowSelection}
          style={tableStyles}
          dataSource={digitalAssetCompanies}
          rowKey={(record) => record.id}
          columns={tableColumns}
          loading={digitalAssetCompaniesProgress === Progress.WORK}
          scroll={digitalAssetCompanies && digitalAssetCompanies.length > 0 ? { y: height } : {}}
          pagination={assetCompanyPagination}
          onChange={onAssetCompaniesChange}
        />
        {assetIsAllSelected && (
          <div style={allSelectedStyle}>
            {t('digitalAsset:form.allCompaniesSelected', { total: assetCompanyTotal })}
          </div>
        )}
      </Flex>
    </Flex>
  )
}

export default CompanyTableTransfer
